import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import theme from '../utils/theme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getStoreSettings } from '../utils/saveStoreSettings';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const DarkModeContext = createContext();

export const useDarkMode = () => useContext(DarkModeContext);

/** Global Faster Link (landing / non-store) preference — unchanged. */
export const GLOBAL_DARK_MODE_KEY = 'isDarkMode';

/** Store-scoped preference key. */
export const storeDarkModeKey = (slug) => `isDarkMode-${slug}`;

const RESERVED_PATHS = new Set([
  'join-network',
  'get-mony',
  'brand-network',
  'privacy-policy',
  'terms-of-use',
]);

function getStoreSlugFromPathname(pathname) {
  const first = (pathname || '/').split('/').filter(Boolean)[0];
  if (!first || RESERVED_PATHS.has(first) || first.includes('.')) return null;
  return first;
}

function readDarkMode(storageKey, fallback) {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved !== null) return JSON.parse(saved);
  } catch (_) {
    /* ignore corrupt values */
  }
  return fallback;
}

export const DarkModeProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const storeSlug = getStoreSlugFromPathname(pathname);
  // Store routes: per-slug key. Landing / reserved routes: global Faster Link key.
  const storageKey = storeSlug ? storeDarkModeKey(storeSlug) : GLOBAL_DARK_MODE_KEY;

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const initialSlug = getStoreSlugFromPathname(
      typeof window !== 'undefined' ? window.location.pathname : pathname
    );
    if (initialSlug) {
      // First visit to a store → Light Mode
      return readDarkMode(storeDarkModeKey(initialSlug), false);
    }
    // Global Faster Link app — keep previous behavior
    return readDarkMode(GLOBAL_DARK_MODE_KEY, prefersDarkMode);
  });

  // Reload preference when switching stores or leaving/entering store routes
  useEffect(() => {
    if (storeSlug) {
      setIsDarkMode(readDarkMode(storageKey, false));
    } else {
      setIsDarkMode(readDarkMode(GLOBAL_DARK_MODE_KEY, prefersDarkMode));
    }
  }, [storeSlug, storageKey, prefersDarkMode]);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem(storageKey, JSON.stringify(newMode));
      return newMode;
    });
  }, [storageKey]);

  const settings = getStoreSettings();
  const primaryColor = settings?.identity_color || '#3C1FAB';
  const currentTheme = theme(primaryColor, isDarkMode ? 'dark' : 'light', i18n.language);

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode, storeSlug }}>
      <MuiThemeProvider theme={currentTheme}>
        {children}
      </MuiThemeProvider>
    </DarkModeContext.Provider>
  );
};
